<template>
  <div class="search-result-page min-h-screen bg-[#f5f7fa] dark:bg-slate-950 pb-20 transition-colors" :class="{ 'electron-search-result-wrap': isElectron, 'search-result-h5': isH5 }">
    <div class="max-w-7xl mx-auto px-1 sm:px-6 lg:px-8 py-8">

      <!-- Filter Tabs -->
      <div class="flex items-center gap-4 mb-8 flex-wrap">
        <span class="font-bold text-slate-700 dark:text-slate-300 text-sm hidden md:block">内容分类</span>
        <div class="flex bg-white dark:bg-slate-800 rounded-lg p-1 shadow-sm border border-slate-100/50 dark:border-slate-700 md:ml-0 overflow-x-auto hide-scrollbar">
          <button
            v-for="item in contentClassify"
            :key="item.key"
            @click="changeContentType(item.key)"
            class="px-4 py-1.5 rounded-md text-sm font-bold transition-all border-none cursor-pointer shrink-0 whitespace-nowrap"
            :class="item.key === activeContentType
              ? 'bg-brand-50 dark:bg-slate-700 text-brand-600 dark:text-brand-400'
              : 'bg-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
          >
            {{ item.name }}
          </button>
        </div>

        <!-- Course sub-filters -->
        <template v-if="activeContentType === 'course'">
          <div class="flex bg-white dark:bg-slate-800 rounded-lg p-1 shadow-sm border border-slate-100/50 dark:border-slate-700 overflow-x-auto hide-scrollbar">
            <button
              v-for="item in courseClassify"
              :key="item.key"
              @click="changeCourseType(item.key)"
              class="px-4 py-1.5 rounded-md text-sm font-bold transition-all border-none cursor-pointer shrink-0 whitespace-nowrap"
              :class="item.key === activeCourseType
                ? 'bg-brand-50 dark:bg-slate-700 text-brand-600 dark:text-brand-400'
                : 'bg-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
            >
              {{ item.name }}
            </button>
          </div>

          <!-- Sort dropdown -->
          <el-dropdown trigger="click" @command="handleMenuBehavior">
            <span class="text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:text-brand-600 transition-colors font-medium">
              {{ sortLabel }} <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item v-for="opt in sortOptions" :key="opt.value" :command="opt.value" :class="{ 'is-active': order_by === opt.value }">{{ opt.label }}</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
      </div>

      <!-- All tab -->
      <div v-if="activeContentType === 'all'">
        <!-- Courses -->
        <div v-if="courseNum" class="mb-12">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
              课程
              <span class="text-slate-400 font-medium text-base">({{ courseNum }})</span>
            </h2>
            <button
              class="text-xs text-slate-500 hover:text-brand-600 dark:hover:text-brand-400 flex items-center gap-1 transition-colors bg-transparent border-none cursor-pointer font-medium"
              @click="changeContentType('course')"
            >
              查看更多
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </button>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <CourseCard
              v-for="item in courseList.slice(0, 4)"
              :key="item.id"
              :courseInfo="item"
            />
          </div>
        </div>

        <!-- Institutions -->
        <div v-if="orgNum" class="mb-12">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
              机构
              <span class="text-slate-400 font-medium text-base">({{ orgNum }})</span>
            </h2>
            <button
              class="text-xs text-slate-500 hover:text-brand-600 dark:hover:text-brand-400 flex items-center gap-1 transition-colors bg-transparent border-none cursor-pointer font-medium"
              @click="changeContentType('org')"
            >
              查看更多
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </button>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <OrgCard
              v-for="item in orgList.slice(0, 3)"
              :key="item.org_id"
              :orgInfo="item"
            />
          </div>
        </div>

        <!-- Empty -->
        <EmptyState v-if="!courseNum && !orgNum" type="search" message="暂无搜索结果" />
      </div>

      <!-- Course tab -->
      <template v-else-if="activeContentType === 'course'">
        <div v-if="courseList.length" class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <CourseCard
            v-for="item in courseList"
            :key="item.id"
            :courseInfo="item"
          />
        </div>
        <EmptyState v-else type="course" message="暂无课程" />
      </template>

      <!-- Institution tab -->
      <template v-else-if="activeContentType === 'org'">
        <div v-if="orgList.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <OrgCard v-for="item in orgList" :key="item.org_id" :orgInfo="item" />
        </div>
        <EmptyState v-else type="org" message="暂无机构" />
      </template>

    </div>
  </div>
</template>

<script>
import { getOrganizationList } from "@saas/server-api/src/organization";
import OrgCard from "./components/OrgCard";
import CourseCard from "@saas/pages/src/home/components/CourseCard";
import { EmptyState } from "@saas/components/src";
import { EventBus } from "@saas/components/src/utils/event-bus";
import { getCourseList } from "@saas/server-api/src/course";
import { getCourseState } from "@saas/server-api/src/course";
import { isH5 } from "@saas/components/src/utils/utils";
export default {
  data: function () {
    return {
      order_by: "0",
      isElectron: window.isElectron,
      isH5: isH5(),
      activeContentType: "all",
      activeCourseType: "all",
      contentClassify: [
        { key: "all", name: "全部" },
        { key: "course", name: "课程" },
        { key: "org", name: "机构" },
      ],
      courseClassify: [
        { key: "all", name: "全部" },
        { key: "free", name: "免费" },
        { key: "charge", name: "收费" },
      ],
      keyword: "",
      orgNum: 0,
      courseNum: 0,
      freeType: {
        all: "",
        free: 1,
        charge: 2,
      },
      courseList: [],
      orgList: [],
      orgPage: { page_no: 1, page_size: 24 },
      coursePage: { page_no: 1, page_size: 12 },
      noMoreOrg: false,
      noMoreCourse: false,
      loading: false,
      sortOptions: [
        { value: "0", label: "推荐排序" },
        { value: "1", label: "价格从低到高" },
        { value: "2", label: "价格从高到低" },
        { value: "3", label: "报名人数从少到多" },
        { value: "4", label: "报名人数从多到少" },
      ],
    };
  },
  computed: {
    sortLabel() {
      const opt = this.sortOptions.find(o => o.value === this.order_by);
      return opt ? opt.label : "推荐排序";
    },
  },
  mounted() {
    this.initData();
    this.$nextTick(() => {
      if (window.isElectron) {
        let elMain = document.getElementById("electron-main");
        if (!elMain) return;
        elMain.addEventListener("scroll", this.handleElScrollFn, true);
      } else {
        window.addEventListener("scroll", this.scrollFn, true);
      }
    });
    EventBus.$off("keyword");
    EventBus.$on("keyword", (msg) => {
      if (msg != this.keyword) {
        this.$router.replace({
          path: this.$route.path,
          query: { ...this.$route.query, keyword: msg },
        });
        this.keyword = msg;
        this.changeContentType();
      }
    });
  },
  beforeDestroy() {
    if (window.isElectron) {
      let elMain = document.getElementById("electron-main");
      if (!elMain) return;
      elMain.removeEventListener("scroll", this.handleElScrollFn, true);
    } else {
      window.removeEventListener("scroll", this.scrollFn, true);
    }
  },
  methods: {
    handleMenuBehavior(command) {
      this.coursePage = { page_no: 1, page_size: 12 };
      this.order_by = command;
      this.contScrollTop();
      this.apiGetCourseList();
    },
    scrollFn() {
      if (this.activeContentType === "all" || this.loading) return;
      let noMore =
        this.activeContentType === "course"
          ? this.noMoreCourse
          : this.noMoreOrg;
      if (noMore) return false;
      var htmlHeight =
        document.body.scrollHeight || document.documentElement.scrollHeight;
      var clientHeight =
        document.body.clientHeight || document.documentElement.clientHeight;
      var scrollTop =
        document.body.scrollTop || document.documentElement.scrollTop;
      if (scrollTop + clientHeight > htmlHeight - 100) {
        if (this.activeContentType === "course") {
          this.coursePage.page_no++;
          this.apiGetCourseList();
        } else {
          this.orgPage.page_no++;
          this.apiGetOrganizationList();
        }
      }
    },
    handleElScrollFn(event) {
      if (this.activeContentType === "all" || this.loading) return;
      let noMore =
        this.activeContentType === "course"
          ? this.noMoreCourse
          : this.noMoreOrg;
      if (noMore) return false;
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        if (this.activeContentType === "course") {
          this.coursePage.page_no++;
          this.apiGetCourseList();
        } else {
          this.orgPage.page_no++;
          this.apiGetOrganizationList();
        }
      }
    },
    initData() {
      if (this.$route.query.keyword) {
        this.keyword = this.$route.query.keyword;
        this.apiGetCourseList("init");
        this.apiGetOrganizationList("init");
      }
    },
    apiGetCourseList(type) {
      this.loading = true;
      let params = {
        ...this.coursePage,
        org_id: "",
        course_name: this.keyword,
        fee_type: this.freeType[this.activeCourseType],
        order_by: Number(this.order_by) || "",
      };
      if (type === "init") {
        params = { ...params, page_no: 1, page_size: 4 };
      }
      getCourseList(params)
        .then((res) => {
          if (res.code === 1) {
            if (type === "init") {
              this.courseNum = res?.data?.total || 0;
              this.courseList = res.data?.list || [];
            } else {
              if (params.page_no === 1) {
                this.courseList = res?.data?.list || [];
              } else {
                this.courseList = this.courseList.concat(res?.data?.list || []);
              }
              if (this.courseList.length === res?.data?.total) {
                this.noMoreCourse = true;
              } else {
                this.noMoreCourse = false;
              }
            }
            if (res?.data?.list?.length) {
              let list = res.data.list;
              let courseIds = list.map((course) => course.id);
              getCourseState({
                course_id: courseIds.join(","),
              }).then((res) => {
                if (res.code === 1 && res.data?.length) {
                  let haveStatusIds = res.data.map((item) => item.course_id);
                  list.forEach((course) => {
                    if (haveStatusIds.includes(course.id)) {
                      let { trial_state, live_state } = res.data.find(
                        (element) => element.course_id === course.id
                      );
                      this.$set(course, "trial_state", trial_state);
                      this.$set(course, "live_state", live_state);
                    }
                  });
                }
              });
            }
          } else {
            this.$message.error(res.message);
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    apiGetOrganizationList(type) {
      let params = {
        org_name: this.keyword,
        ...this.orgPage,
      };
      if (type === "init") {
        params = { ...params, page_no: 1, page_size: 3 };
      }
      getOrganizationList(params).then((res) => {
        if (res.code === 1) {
          if (type === "init") {
            this.orgNum = res?.data?.total || 0;
            this.orgList = res?.data?.list || [];
          } else {
            if (params.page_no === 1) {
              this.orgList = res?.data?.list || [];
            } else {
              this.orgList = this.orgList.concat(res?.data?.list || []);
            }
            if (this.orgList.length === res?.data?.total) {
              this.noMoreOrg = true;
            } else {
              this.noMoreOrg = false;
            }
          }
        }
      });
    },
    contScrollTop() {
      if (window.isElectron) {
        let elMain = document.getElementById("electron-main");
        if (!elMain) return;
        elMain.scrollTop = 0;
      } else {
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    },
    changeContentType(key) {
      if (key === this.activeContentType) return false;
      this.contScrollTop();
      if (key) {
        this.activeContentType = key;
      } else {
        key = this.activeContentType;
      }
      if (key === "all") {
        this.apiGetCourseList("init");
        this.apiGetOrganizationList("init");
      } else if (key === "course") {
        this.coursePage = { page_no: 1, page_size: 12 };
        this.apiGetCourseList();
      } else {
        this.orgPage = { page_no: 1, page_size: 24 };
        this.apiGetOrganizationList();
      }
    },
    changeCourseType(key) {
      if (key === this.activeCourseType) return false;
      this.contScrollTop();
      this.activeCourseType = key;
      this.coursePage = { page_no: 1, page_size: 12 };
      this.apiGetCourseList();
    },
  },
  components: {
    CourseCard,
    OrgCard,
    EmptyState,
  },
};
</script>

<style lang="scss" scoped>
.search-result-page {
  min-height: calc(100vh - 80px);
}

// Electron 适配
.electron-search-result-wrap {
  .max-w-7xl {
    max-width: 980px;
  }
}

// H5 手机端适配 (覆盖 Tailwind 在 amfe-flexible 下失效的 rem 大小)
.search-result-h5 {
  padding-bottom: 20px;

  // 顶部容器
  .max-w-7xl {
    padding: 16px 12px;
  }

  // 筛选区域组合
  .gap-4 {
    gap: 12px;
  }
  .mb-8 {
    margin-bottom: 16px;
  }
  
  // 文字和按钮大小
  .text-sm {
    font-size: 13px;
    line-height: 18px;
  }
  .px-4 {
    padding-left: 12px;
    padding-right: 12px;
  }
  .py-1\.5 {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  // 标题和 "查看更多"
  .mb-12 {
    margin-bottom: 24px;
  }
  .mb-6 {
    margin-bottom: 12px;
  }
  .text-xl {
    font-size: 18px;
  }
  .text-base {
    font-size: 14px;
  }
  .text-xs {
    font-size: 12px;
  }

  // Grid
  .gap-6 {
    gap: 12px;
  }

  // Org 卡片适配
  ::v-deep .org-card {
    padding: 12px;
    gap: 12px;
    border-radius: 12px;

    .w-16.h-16 {
      width: 80px;
      height: 80px;
      border-radius: 8px;
    }

    .text-base {
      font-size: 15px;
      margin-bottom: 4px;
    }

    .text-xs {
      font-size: 12px;
      margin-bottom: 4px;
    }
  }
}
</style>
